// src/StaffDocuments.js

import React, { useState, useEffect } from 'react';
import { db, storage } from './firebase';
import { collection, addDoc, query, orderBy, onSnapshot, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useUser } from './UserContext';
import { FaFilePdf, FaFileWord, FaFileImage, FaTrash, FaDownload, FaLock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

// Stili inline per semplicità (o aggiungi a StaffModal.css)
const styles = {
    container: { padding: '10px 0' },
    uploadBox: { border: '2px dashed #ccc', borderRadius: '8px', padding: '20px', textAlign: 'center', marginBottom: '20px', backgroundColor: '#f9f9f9' },
    docList: { listStyle: 'none', padding: 0 },
    docItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #eee', transition: 'background 0.2s' },
    docInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
    meta: { fontSize: '0.8rem', color: '#666' },
    actions: { display: 'flex', gap: '10px' }
};

const getIcon = (type) => {
    if (type.includes('pdf')) return <FaFilePdf color="#dc3545" size={20} />;
    if (type.includes('word') || type.includes('document')) return <FaFileWord color="#0d6efd" size={20} />;
    if (type.includes('image')) return <FaFileImage color="#198754" size={20} />;
    return <FaLock color="#6c757d" size={20} />;
};

export default function StaffDocuments({ targetUserId }) {
    const { userProfile } = useUser();
    const [documents, setDocuments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [docType, setDocType] = useState('Contratto');
    const [description, setDescription] = useState('');

    // Permessi
    const isManager = userProfile?.permissions?.staff === 'completo' || userProfile?.role === 'Super Admin' || userProfile?.role === 'Proprietario';
    // Se è l'utente stesso, può solo vedere (a meno che non sia anche manager, ma qui limitiamo la scrittura per policy HR)
    const canWrite = isManager; 

    useEffect(() => {
        if (!userProfile?.centerId || !targetUserId) return;

        // Percorso: centers/{cid}/staff/{uid}/privateDocuments
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'staff', targetUserId, 'privateDocuments'),
            orderBy('uploadedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setDocuments(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (error) => {
            console.error("Errore lettura documenti privati:", error);
            // Non mostrare toast di errore se è solo questione di permessi (es. caricamento iniziale)
        });

        return () => unsubscribe();
    }, [userProfile, targetUserId]);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const uniqueName = `${Date.now()}_${uuidv4()}.${fileExt}`;
            const storagePath = `staff_documents/${userProfile.centerId}/${targetUserId}/${uniqueName}`;
            
            // 1. Upload Storage
            const fileRef = ref(storage, storagePath);
            await uploadBytes(fileRef, file);
            const downloadUrl = await getDownloadURL(fileRef);

            // 2. Save Firestore Reference
            await addDoc(collection(db, 'centers', userProfile.centerId, 'staff', targetUserId, 'privateDocuments'), {
                name: description || file.name,
                originalName: file.name,
                type: docType,
                mimeType: file.type,
                url: downloadUrl,
                storagePath: storagePath,
                uploadedAt: serverTimestamp(),
                uploadedBy: userProfile.name
            });

            toast.success("Documento archiviato con successo.");
            setDescription('');
            // Reset input file
            e.target.value = null;

        } catch (error) {
            console.error("Errore upload:", error);
            toast.error("Errore durante il caricamento.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (docData) => {
        if (!window.confirm("Sei sicuro di voler eliminare definitivamente questo documento?")) return;

        try {
            // 1. Delete from Storage
            const fileRef = ref(storage, docData.storagePath);
            await deleteObject(fileRef).catch(err => console.warn("File storage non trovato, elimino solo ref db"));

            // 2. Delete from Firestore
            await deleteDoc(doc(db, 'centers', userProfile.centerId, 'staff', targetUserId, 'privateDocuments', docData.id));
            
            toast.success("Documento eliminato.");
        } catch (error) {
            console.error("Errore eliminazione:", error);
            toast.error("Impossibile eliminare il documento.");
        }
    };

    return (
        <div style={styles.container}>
            {/* UPLOAD AREA (Solo per Manager) */}
            {canWrite && (
                <div style={styles.uploadBox}>
                    <h4 style={{marginTop:0, color:'#555'}}>Carica Documento Riservato</h4>
                    <div style={{display:'grid', gap:'10px', maxWidth:'400px', margin:'0 auto'}}>
                        <select 
                            value={docType} 
                            onChange={(e) => setDocType(e.target.value)}
                            style={{padding:'8px', borderRadius:'4px', border:'1px solid #ccc'}}
                        >
                            <option value="Contratto">Contratto</option>
                            <option value="Lettera">Lettera / Comunicazione</option>
                            <option value="Contestazione">Contestazione Disciplinare</option>
                            <option value="Documento Identità">Documento Identità</option>
                            <option value="Certificato">Certificato</option>
                            <option value="Altro">Altro</option>
                        </select>
                        <input 
                            type="text" 
                            placeholder="Descrizione (es. Contratto 2024 firmato)" 
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{padding:'8px', borderRadius:'4px', border:'1px solid #ccc'}}
                        />
                        <input 
                            type="file" 
                            onChange={handleUpload} 
                            disabled={isUploading}
                            style={{marginTop:'10px'}}
                        />
                        {isUploading && <p style={{color:'#0d6efd'}}>Caricamento in corso...</p>}
                    </div>
                </div>
            )}

            {/* LIST AREA */}
            <h4 style={{marginBottom:'15px'}}>Archivio Digitale ({documents.length})</h4>
            {documents.length === 0 ? (
                <p style={{color:'#999', fontStyle:'italic'}}>Nessun documento presente in archivio.</p>
            ) : (
                <ul style={styles.docList}>
                    {documents.map(doc => (
                        <li key={doc.id} style={styles.docItem}>
                            <div style={styles.docInfo}>
                                {getIcon(doc.mimeType || '')}
                                <div>
                                    <div style={{fontWeight:'600', color:'#333'}}>{doc.name}</div>
                                    <div style={styles.meta}>
                                        {doc.type} • Caricato il {doc.uploadedAt ? moment(doc.uploadedAt.toDate()).format('DD/MM/YYYY') : '-'} da {doc.uploadedBy}
                                    </div>
                                </div>
                            </div>
                            <div style={styles.actions}>
                                <a 
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="icon-button"
                                    title="Scarica / Visualizza"
                                    style={{color:'#0d6efd', fontSize:'1.2rem'}}
                                >
                                    <FaDownload />
                                </a>
                                {canWrite && (
                                    <button 
                                        onClick={() => handleDelete(doc)}
                                        className="icon-button"
                                        title="Elimina Definitivamente"
                                        style={{color:'#dc3545', fontSize:'1.1rem'}}
                                    >
                                        <FaTrash />
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}